import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Offcanvas, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";
import {
  BookingVisaForm,
  VISA_TYPE_LABEL,
  COUNTRY_LIST,
  addVisaBooking,
  sanitizeFileName,
} from "src/store/reducers/bookings";
import { useAppDispatch } from "src/store/hooks";
import CustomDatePickerWithLabel from "src/layouts/form/CustomDateTimeInputWithLabel";
import { KeyRequired } from "src/layouts/form/validations/Validation";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import CustomSelectWithLabel from "src/layouts/form/CustomSelectWithLabel";
import CustomTextAreaWithLabel from "src/layouts/form/CustomTextAreaWithLabel";

const ACCEPTED_FILE_TYPES = ".jpg,.jpeg,.png,.pdf";

type IAddVisaBookingCanvas = {
  Title: string;
  show: boolean;
  bookingId: string;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
};

const AddVisaBookingCanvas = ({
  Title,
  show,
  bookingId,
  setShow,
}: IAddVisaBookingCanvas) => {
  const [pending, setPending] = useState(false);
  const dispatch = useAppDispatch();
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    handleChange,
    values,
    handleSubmit,
    resetForm,
    setFieldValue,
    touched,
    errors,
    setErrors,
  } = useFormik<{
    visa_holder_name: string;
    visa_number: string;
    visa_type: number;
    issuing_country: string;
    application_requirements: string;
    validity_period: string;
    fees: string;
    currency: string;
    advance_amount_paid: string;
    processing_time: string;
    additional_notes: string;
  }>({
    initialValues: {
      visa_holder_name: "",
      visa_number: "",
      visa_type: 1,
      issuing_country: "",
      application_requirements: "",
      validity_period: "",
      fees: "",
      currency: "",
      advance_amount_paid: "",
      processing_time: "",
      additional_notes: "",
    },
    validationSchema: Yup.object({
      visa_holder_name: KeyRequired("Visa holder name"),
      visa_number: KeyRequired("Visa number"),
      visa_type: KeyRequired("Visa type"),
      issuing_country: KeyRequired("Issuing country"),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      setPending(true);
      addVisa({ ...values, booking: bookingId });
    },
  });

  const addVisa = useCallback(
    async (values: BookingVisaForm) => {
      try {
        await dispatch(addVisaBooking({ form: values, files })).unwrap();
        toast.success("Visa is added successfully");
        setShow(false);
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, files, setErrors, setShow]
  );

  useEffect(() => {
    resetForm();
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [resetForm, show]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...newFiles]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => setShow(false)}
      className="offcanvas-end customeoff"
      placement="end"
    >
      <div className="offcanvas-header">
        <h3 className="modal-title" id="#gridSystemModal">
          {Title}
        </h3>
        <button
          type="button"
          className="btn-close"
          onClick={() => setShow(false)}
        >
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div className="offcanvas-body">
        <div className="container-fluid">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="visa_holder_name"
                  labelText="Visa Holder Name"
                  required={true}
                  handleChange={handleChange}
                  value={values.visa_holder_name}
                  touched={touched.visa_holder_name}
                  errors={errors.visa_holder_name}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="visa_number"
                  labelText="Visa Number"
                  required={true}
                  handleChange={handleChange}
                  value={values.visa_number}
                  touched={touched.visa_number}
                  errors={errors.visa_number}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomSelectWithLabel
                  id="visa_type"
                  labelText="Visa Type"
                  required={true}
                  handleChange={handleChange}
                  value={values.visa_type}
                  touched={touched.visa_type}
                  errors={errors.visa_type}
                >
                  {Object.entries(VISA_TYPE_LABEL).map(([key, label]) => (
                    <option value={key} key={key}>
                      {label}
                    </option>
                  ))}
                </CustomSelectWithLabel>
              </div>
              <div className="col-xl-6 mb-3">
                <CustomSelectWithLabel
                  id="issuing_country"
                  labelText="Issuing Country"
                  required={true}
                  handleChange={handleChange}
                  value={values.issuing_country}
                  touched={touched.issuing_country}
                  errors={errors.issuing_country}
                >
                  <option value="">Select Country</option>
                  {COUNTRY_LIST.map((country) => (
                    <option value={country.country_name} key={country.iso_alpha2}>
                      {country.country_name}
                    </option>
                  ))}
                </CustomSelectWithLabel>
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="validity_period"
                  labelText="Validity Date"
                  inputType="date"
                  handleChange={handleChange}
                  value={values.validity_period}
                  touched={touched.validity_period}
                  errors={errors.validity_period}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="processing_time"
                  labelText="Processing Time"
                  handleChange={handleChange}
                  value={values.processing_time}
                  touched={touched.processing_time}
                  errors={errors.processing_time}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="fees"
                  labelText="Fees"
                  handleChange={handleChange}
                  value={values.fees}
                  touched={touched.fees}
                  errors={errors.fees}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="currency"
                  labelText="Currency"
                  handleChange={handleChange}
                  value={values.currency}
                  touched={touched.currency}
                  errors={errors.currency}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="advance_amount_paid"
                  labelText="Advance Amount Paid"
                  handleChange={handleChange}
                  value={values.advance_amount_paid}
                  touched={touched.advance_amount_paid}
                  errors={errors.advance_amount_paid}
                />
              </div>
              <div className="col-xl-12 mb-3">
                <CustomTextAreaWithLabel
                  id="application_requirements"
                  labelText="Application Requirements"
                  handleChange={handleChange}
                  value={values.application_requirements}
                  touched={touched.application_requirements}
                  errors={errors.application_requirements}
                />
              </div>
              <div className="col-xl-12 mb-3">
                <CustomTextAreaWithLabel
                  id="additional_notes"
                  labelText="Additional Notes"
                  handleChange={handleChange}
                  value={values.additional_notes}
                  touched={touched.additional_notes}
                  errors={errors.additional_notes}
                />
              </div>

              <div className="col-xl-12 mb-3">
                <Form.Label>Documents</Form.Label>
                <Form.Control
                  type="file"
                  accept={ACCEPTED_FILE_TYPES}
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                <Form.Text className="text-muted">
                  Accepted formats: JPG, JPEG, PNG, PDF (Max 10 files, 5MB each)
                </Form.Text>
                {files.length > 0 && (
                  <div className="mt-2">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center justify-content-between mb-1 p-2 bg-light rounded"
                      >
                        <span className="text-truncate me-2">
                          {sanitizeFileName(file.name)}
                        </span>
                        <button
                          type="button"
                          className="btn btn-danger btn-xs"
                          onClick={() => removeFile(index)}
                        >
                          <i className="fas fa-x" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={pending}
              className="btn btn-primary me-1"
            >
              Submit
            </button>
            <Link
              to={"#"}
              onClick={() => setShow(false)}
              className="btn btn-danger light ms-1"
            >
              Cancel
            </Link>
          </form>
        </div>
      </div>
    </Offcanvas>
  );
};

export default AddVisaBookingCanvas;
